// One client: the page, contacts, properties, deactivate, merge.
import {
  type ClientDetail,
  CONTACT_TYPE_WORDS,
  type ContactType,
  fullAddress,
  INACTIVE_REASON_WORDS,
  INACTIVE_REASONS,
  type InactiveReason,
  type PropertyRow,
} from "@steward/shared";
import { useNavigate } from "@tanstack/react-router";
import { type SubmitEvent, useState } from "react";
import {
  useAddContact,
  useClient,
  useClients,
  useDeactivateClient,
  useMergeClient,
  useReactivateClient,
  useRemoveContact,
  useSaveProperty,
  useStartThread,
} from "../../api/hooks";
import { useCardLink } from "../../api/money";
import { useSession } from "../../app/session";
import { Choice, Combobox, InputText } from "../../components/fields";
import { moneyExact, phoneText, plural, shortDate } from "../../components/format";
import { Icon } from "../../components/Icon";
import { NextStep } from "../../components/next";
import { Menu, Modal, useToast } from "../../components/overlays";
import { Banner, Button, Card, IconButton, LoadError, Page, Skeleton, StatusLabel } from "../../components/parts";
import { PhoneRecord } from "../../components/PhoneRecord";
import { PlanOfferSend } from "../../components/PlanOffer";
import { usePhone } from "../../components/usePhone";
import { errText, FilesCard, NotesCard } from "../../components/work";
import {
  ClientBilling,
  ClientFacts,
  ClientSchedule,
  CreateForClient,
  PaymentMethods,
  RecentPricing,
  WorkOverview,
} from "../ClientCards";
import { ClientInbox } from "../Inbox";
import { ContactTypeSelect } from "./Form";
import { LEAD_WORDS } from "./List";
import { EMPTY_PLACE, type PlaceDraft, PlaceFields, placeOf, placeOut } from "./Place";

export function ClientPage({ id }: { id: string }) {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const client = useClient(id);
  const reactivate = useReactivateClient();
  const cardLink = useCardLink();
  const [sheet, setSheet] = useState<"deactivate" | "merge" | "offer" | null>(null);
  const [tab, setTab] = useState<"info" | "talk">("info");
  const startThread = useStartThread();
  const [openThread, setOpenThread] = useState<string | null>(null);
  const phone = usePhone();
  const c = client.data;
  if (client.error)
    return (
      <Page title="Client">
        <LoadError error={client.error} retry={() => void client.refetch()} />
      </Page>
    );
  if (!c)
    return (
      <Page title={<span className="skeleton inline wide" />}>
        <Skeleton rows={6} />
      </Page>
    );
  const editable = can("client.update");
  const status = !c.active ? (
    <StatusLabel status="inactive">
      {c.inactiveReason ? `Not active · ${INACTIVE_REASON_WORDS[c.inactiveReason]}` : "Not active"}
    </StatusLabel>
  ) : c.atRisk ? (
    <span title={c.atRisk}>
      <StatusLabel status="critical">At risk</StatusLabel>
    </span>
  ) : null;
  // They reviewed us on Google (matched by name), so the review request skips them.
  const gr = c.googleReview;
  const reviewed = gr ? (
    <span title={gr.at ? `Reviewed on Google ${shortDate(gr.at.slice(0, 10))}` : "Reviewed on Google"}>
      <StatusLabel status="success">
        {gr.stars ? `Google review ${String(gr.stars)} ${gr.stars === 1 ? "star" : "stars"}` : "Reviewed on Google"}
      </StatusLabel>
    </span>
  ) : null;
  const facts = [
    c.kind === "business" ? "Business" : "Home",
    `Client since ${shortDate(c.createdAt.slice(0, 10))}`,
    c.lifetimeCents ? `Paid ${moneyExact(c.lifetimeCents)} so far` : null,
    c.active && c.atRisk ? c.atRisk : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const newJob =
    can("job.update") && c.active ? (
      <Button icon="plus" onClick={() => void navigate({ to: "/jobs/new", search: { clientId: c.id } })}>
        New job
      </Button>
    ) : null;
  const leadWords = c.leadSource ? (LEAD_WORDS[c.leadSource] ?? c.leadSource) : null;
  // Jobber's envelope and bubble: open this client's email or text conversation, ready to write.
  const talk = (channel: "sms" | "email") =>
    startThread.mutate(
      { clientId: c.id, channel },
      {
        onSuccess: (r) => {
          setOpenThread(r.id);
          setTab("talk");
        },
        onError: (e) => toast(errText(e), { kind: "error" }),
      },
    );
  const talkBtns =
    editable && (c.phone || c.email) ? (
      <>
        {c.email ? <IconButton icon="mail" label="Email" onClick={() => talk("email")} /> : null}
        {c.phone ? <IconButton icon="message" label="Text" onClick={() => talk("sms")} /> : null}
      </>
    ) : null;
  const editBtn = editable ? (
    <Button type="secondary" icon="edit" onClick={() => void navigate({ to: "/clients/$id/edit", params: { id: c.id } })}>
      Edit
    </Button>
  ) : null;
  // Offer a plan: a one-time home client gets their prices and a link to book their own schedule (owner, 2026-11-02).
  const offers = company.trade === "cleaning" && c.kind === "home" && can("quote.update");
  const moreMenu =
    editable || can("client.view_hub") || can("dispute.read") || offers ? (
      <Menu align="right" button={({ toggle }) => <IconButton icon="more" label="More" onClick={toggle} />}>
        {(close) => (
          <>
            {offers ? (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  setSheet("offer");
                }}
              >
                Offer a plan
              </button>
            ) : null}
            {can("dispute.read") ? (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  void navigate({ to: "/clients/$id/dispute", params: { id: c.id } });
                }}
              >
                Dispute file
              </button>
            ) : null}
            {can("client.view_hub") ? (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  void navigate({ to: "/clients/$id/as-client", params: { id: c.id } });
                }}
              >
                View as client
              </button>
            ) : null}
            {!editable ? null : c.active ? (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  setSheet("deactivate");
                }}
              >
                Set not active
              </button>
            ) : (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  reactivate.mutate(c.id, {
                    onSuccess: () => toast(`${c.name} is active again`),
                    onError: (e) => toast(errText(e), { kind: "error" }),
                  });
                }}
              >
                Make active again
              </button>
            )}
            {can("client.merge") ? (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  setSheet("merge");
                }}
              >
                Merge with another client
              </button>
            ) : null}
            {can("invoice.update") && c.active ? (
              <>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    close();
                    cardLink.mutate(
                      { clientId: c.id, send: true },
                      {
                        onSuccess: () => toast(`Card link texted to ${c.name}. Nothing is charged.`),
                        onError: (e) => toast(errText(e), { kind: "error" }),
                      },
                    );
                  }}
                >
                  Text a link to save a card
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    close();
                    cardLink.mutate(
                      { clientId: c.id, send: false },
                      {
                        onSuccess: (r) =>
                          void navigator.clipboard.writeText(r.url).then(
                            () => toast("Card link copied"),
                            () => toast(r.url),
                          ),
                        onError: (e) => toast(errText(e), { kind: "error" }),
                      },
                    );
                  }}
                >
                  Copy a link to save a card
                </button>
              </>
            ) : null}
          </>
        )}
      </Menu>
    ) : null;
  const banners = (
    <>
      <NextStep freshKey={`client:${c.id}`} title={`${c.name} is added. What comes next?`}>
        {can("quote.update") ? (
          <Button
            type="secondary"
            size="small"
            icon="quotes"
            onClick={() => void navigate({ to: "/quotes/new", search: { clientId: c.id } })}
          >
            Send a quote
          </Button>
        ) : null}
        {can("job.update") ? (
          <Button
            type="secondary"
            size="small"
            icon="jobs"
            onClick={() => void navigate({ to: "/jobs/new", search: { clientId: c.id } })}
          >
            Book the work
          </Button>
        ) : null}
      </NextStep>
      {!c.active && c.inactiveNote ? <Banner type="warning">{c.inactiveNote}</Banner> : null}
      {c.officeNotes ? (
        <Banner type="notice">
          <b>Office note.</b> {c.officeNotes}
        </Banner>
      ) : null}
    </>
  );
  // Jobber's two tabs: everything about the client, and every text and email with them.
  const tabs = can("client.read") ? (
    <div className="segmented page-tabs" role="tablist" aria-label="Client page">
      <button type="button" role="tab" aria-selected={tab === "info"} onClick={() => setTab("info")}>
        Client information
      </button>
      <button type="button" role="tab" aria-selected={tab === "talk"} onClick={() => setTab("talk")}>
        Communication
      </button>
    </div>
  ) : null;
  const sheets = (
    <>
      <DeactivateSheet c={c} open={sheet === "deactivate"} onClose={() => setSheet(null)} />
      {sheet === "offer" ? <PlanOfferSend clientId={c.id} onClose={() => setSheet(null)} /> : null}
      {can("client.merge") ? <MergeSheet c={c} open={sheet === "merge"} onClose={() => setSheet(null)} /> : null}
    </>
  );

  if (phone)
    return (
      <PhoneRecord
        back={{ label: "Back", go: () => void navigate({ to: "/clients" }) }}
        status={
          <>
            {status ?? <StatusLabel status="success">Active</StatusLabel>}
            {reviewed}
          </>
        }
        title={c.name}
        sub={facts}
        phone={c.phone}
        email={c.email}
        facts={[
          ...(c.address
            ? [
                {
                  key: "where",
                  icon: "pin" as const,
                  main: c.address,
                  sub: c.properties.length > 1 ? `Directions · ${String(c.properties.length)} properties` : "Directions",
                  directions: mapsLine(c),
                },
              ]
            : []),
          {
            key: "visits",
            icon: "schedule" as const,
            main: c.nextVisit ? `Next visit ${shortDate(c.nextVisit)}` : "Nothing booked",
            sub: [
              c.lastVisit ? `Last visit ${shortDate(c.lastVisit)}` : null,
              c.activeJobs ? `${String(c.activeJobs)} active ${c.activeJobs === 1 ? "job" : "jobs"}` : null,
            ]
              .filter(Boolean)
              .join(" · "),
          },
        ]}
        actions={
          <>
            {newJob}
            {editBtn}
            {moreMenu}
          </>
        }
      >
        {banners}
        {tabs}
        {tab === "talk" ? (
          <ClientInbox clientId={c.id} />
        ) : (
          <>
            <WorkOverview c={c} />
            <ClientSchedule c={c} />
            <Contacts c={c} editable={editable} />
            <Properties c={c} editable={editable} trade={company.trade} />
            {can("invoice.read") ? (
              <>
                <ClientBilling c={c} />
                <PaymentMethods c={c} />
              </>
            ) : null}
            <NotesCard on="clients" id={c.id} notes={c.notesList} editable={editable} />
            <FilesCard on="clients" id={c.id} files={c.files} editable={editable} title="Files" />
            <RecentPricing c={c} />
            <ClientFacts c={c} leadWords={leadWords} />
          </>
        )}
        {sheets}
      </PhoneRecord>
    );

  return (
    <Page
      title={c.name}
      titleMetaData={
        <>
          {status}
          {reviewed}
        </>
      }
      subtitle={facts}
      primaryAction={<CreateForClient c={c} />}
      secondaryAction={
        <>
          {talkBtns}
          {editBtn}
        </>
      }
      moreActions={moreMenu}
    >
      <ClientFacts c={c} leadWords={leadWords} />
      {banners}
      {tabs}
      {tab === "talk" ? (
        <ClientInbox key={openThread ?? "all"} clientId={c.id} open={openThread} />
      ) : (
        <div className="stack">
          <WorkOverview c={c} />
          <div className="home-grid">
            <div className="stack">
              <Contacts c={c} editable={editable} />
              <NotesCard on="clients" id={c.id} notes={c.notesList} editable={editable} />
            </div>
            <div className="stack">
              <Properties c={c} editable={editable} trade={company.trade} />
              <FilesCard on="clients" id={c.id} files={c.files} editable={editable} title="Files" />
            </div>
          </div>
          {can("invoice.read") ? (
            <>
              <ClientBilling c={c} />
              <PaymentMethods c={c} />
            </>
          ) : null}
          <ClientSchedule c={c} />
          <RecentPricing c={c} />
        </div>
      )}
      {sheets}
    </Page>
  );
}

/** The client's address with its city, state and ZIP from the matching property, so the maps app can find it. */
function mapsLine(c: ClientDetail): string {
  const home = c.properties.find((p) => p.address === c.address);
  return home ? fullAddress(home.address, home) : (c.address ?? "");
}

function Contacts({ c, editable }: { c: ClientDetail; editable: boolean }) {
  const add = useAddContact();
  const remove = useRemoveContact();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [kind, setKind] = useState<"phone" | "email">("phone");
  const [type, setType] = useState<ContactType>("cell");
  const [value, setValue] = useState("");
  const [label, setLabel] = useState("");
  const pickKind = (k: "phone" | "email") => {
    setKind(k);
    setType(k === "phone" ? "cell" : "billing");
  };
  return (
    <Card
      title="Contact"
      action={
        editable && !adding ? (
          <Button type="tertiary" size="small" icon="plus" onClick={() => setAdding(true)}>
            Add
          </Button>
        ) : null
      }
      flush
    >
      <div className="todo">
        {c.contacts.map((x) => (
          <div className="item" key={x.id}>
            <Icon name={x.kind === "phone" ? "phone" : "mail"} />
            <div className="what">
              <a href={x.kind === "phone" ? `tel:${x.value}` : `mailto:${x.value}`}>
                {x.kind === "phone" ? (phoneText(x.value) ?? x.value) : x.value}
              </a>
              <span>
                {[
                  x.type ? CONTACT_TYPE_WORDS[x.type] : null,
                  x.label,
                  x.isMain ? "Main" : null,
                  x.optedOut ? "Said stop to texts" : null,
                ]
                  .filter(Boolean)
                  .join(" · ") || (x.kind === "phone" ? "Phone" : "Email")}
              </span>
            </div>
            {editable && !x.isMain ? (
              <IconButton
                icon="trash"
                label="Remove"
                size={18}
                onClick={() =>
                  remove.mutate({ clientId: c.id, id: x.id }, { onError: (e) => toast(errText(e), { kind: "error" }) })
                }
              />
            ) : null}
          </div>
        ))}
        {!c.contacts.length && !adding ? <div className="card-empty muted">No phone or email yet.</div> : null}
      </div>
      {adding ? (
        <form
          className="note-form"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate(
              {
                clientId: c.id,
                contact: { kind, type, value: value.trim(), ...(label.trim() ? { label: label.trim() } : {}), isMain: false },
              },
              {
                onSuccess: () => {
                  setAdding(false);
                  setValue("");
                  setLabel("");
                },
                onError: (er) => toast(errText(er), { kind: "error" }),
              },
            );
          }}
        >
          <Choice
            label="Kind"
            value={kind}
            onChange={pickKind}
            options={[
              { value: "phone", label: "Phone" },
              { value: "email", label: "Email" },
            ]}
          />
          <ContactTypeSelect kind={kind} value={type} onChange={setType} />
          <InputText
            label={kind === "phone" ? "Phone" : "Email"}
            value={value}
            autoFocus
            type={kind === "phone" ? "tel" : "email"}
            onChange={(e) => setValue(e.target.value)}
          />
          <InputText label="Name" value={label} placeholder="Mike, tenant" onChange={(e) => setLabel(e.target.value)} />
          <div className="row end">
            <Button type="tertiary" size="small" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button size="small" submit loading={add.isPending} disabled={!value.trim()}>
              Save
            </Button>
          </div>
        </form>
      ) : null}
      <div className="card-foot muted">
        {c.prefers === "email" ? "Likes email best" : "Likes texts best"}
        {c.noMarketing ? " · No marketing" : ""}
      </div>
    </Card>
  );
}

function Properties({ c, editable, trade }: { c: ClientDetail; editable: boolean; trade: string }) {
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<PropertyRow | null>(null);
  return (
    <Card
      title={c.properties.length > 1 ? "Properties" : "Property"}
      action={
        editable ? (
          <Button type="tertiary" size="small" icon="plus" onClick={() => setAdding(true)}>
            Add
          </Button>
        ) : null
      }
      flush
    >
      {c.properties.length ? (
        <div className="todo">
          {c.properties.map((p) => (
            <div key={p.id} className="fold">
              <button
                type="button"
                className="item fold-head"
                aria-expanded={open === p.id}
                onClick={() => setOpen(open === p.id ? null : p.id)}
              >
                <Icon name="pin" />
                <div className="what">
                  <b>{p.label ?? p.address}</b>
                  <span>
                    {[p.label ? p.address : null, p.summary || null].filter(Boolean).join(" · ") ||
                      [p.city, p.state].filter(Boolean).join(", ")}
                  </span>
                </div>
                <Icon name={open === p.id ? "chevronDown" : "chevronRight"} size={18} />
              </button>
              {open === p.id ? (
                <dl className="details fold-body">
                  <div>
                    <dt>Address</dt>
                    <dd>{[p.address, p.city, [p.state, p.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ")}</dd>
                  </div>
                  {trade === "cleaning" ? (
                    <div>
                      <dt>Size</dt>
                      <dd>{p.summary || <span className="muted">Not set</span>}</dd>
                    </div>
                  ) : trade === "lawn" ? (
                    <div>
                      <dt>Lawn</dt>
                      <dd>
                        {p.lawnSqft ? `${p.lawnSqft.toLocaleString("en-US")} sq ft` : <span className="muted">Not set</span>}
                      </dd>
                    </div>
                  ) : null}
                  <div>
                    <dt>Gate and alarm</dt>
                    <dd>{p.accessNotes ?? <span className="muted">None</span>}</dd>
                  </div>
                  {p.notes ? (
                    <div>
                      <dt>Notes</dt>
                      <dd>{p.notes}</dd>
                    </div>
                  ) : null}
                  <div>
                    <dt />
                    <dd>
                      <a
                        href={`https://maps.apple.com/?q=${encodeURIComponent([p.address, p.city, p.state].filter(Boolean).join(", "))}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open in maps
                      </a>
                      {editable ? (
                        <Button type="tertiary" size="small" icon="edit" onClick={() => setEditing(p)}>
                          Edit
                        </Button>
                      ) : null}
                    </dd>
                  </div>
                </dl>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <div className="card-empty muted">No address yet.</div>
      )}
      {adding || editing ? (
        <PropertySheet
          clientId={c.id}
          property={editing}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
          trade={trade}
          business={c.kind === "business"}
        />
      ) : null}
    </Card>
  );
}

/** Add a property, or change one (property set): the same address box as a new client, in parts. */
function PropertySheet({
  clientId,
  property,
  onClose,
  trade,
  business,
}: {
  clientId: string;
  property: PropertyRow | null;
  onClose: () => void;
  trade: string;
  business: boolean;
}) {
  const save = useSaveProperty();
  const toast = useToast();
  const [place, setPlace] = useState<PlaceDraft>(() => (property ? placeOf(property) : EMPTY_PLACE));
  const [label, setLabel] = useState(property?.label ?? "");
  const submit = (e?: SubmitEvent) => {
    e?.preventDefault();
    if (place.street1.trim().length < 3) return;
    save.mutate(
      { clientId, ...(property ? { id: property.id } : {}), property: placeOut(place, label, property) },
      {
        onSuccess: () => {
          onClose();
          toast(property ? "Address saved" : "Property added");
        },
        onError: (er) => toast(errText(er), { kind: "error" }),
      },
    );
  };
  return (
    <Modal
      title={property ? "Change the address" : "Add a property"}
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={save.isPending} disabled={place.street1.trim().length < 3} onClick={() => submit()}>
            Save property
          </Button>
        </>
      }
    >
      <form className="client-form" autoComplete="on" onSubmit={submit}>
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
        <PlaceFields p={place} set={setPlace} trade={trade} business={business} />
        <div className="span-6">
          <InputText
            label="Name for it"
            placeholder="Rental, Mom's house"
            autoComplete="off"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </div>
      </form>
    </Modal>
  );
}

function DeactivateSheet({ c, open, onClose }: { c: ClientDetail; open: boolean; onClose: () => void }) {
  const deactivate = useDeactivateClient();
  const reactivate = useReactivateClient();
  const toast = useToast();
  const [reason, setReason] = useState<InactiveReason>("cancelled");
  const [note, setNote] = useState("");
  return (
    <Modal
      title={`Set ${c.name} to not active?`}
      open={open}
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variation="destructive"
            loading={deactivate.isPending}
            onClick={() =>
              deactivate.mutate(
                { id: c.id, body: { reason, ...(note.trim() ? { note: note.trim() } : {}) } },
                {
                  onSuccess: () => {
                    onClose();
                    toast(`${c.name} set to not active`, { undo: () => reactivate.mutate(c.id) });
                  },
                  onError: (e) => toast(errText(e), { kind: "error" }),
                },
              )
            }
          >
            Set not active
          </Button>
        </>
      }
    >
      <div className="stack">
        <p className="muted">
          Their history stays. They drop off the lists and nothing new gets booked. You can make them active again any time.
        </p>
        <Combobox
          label="Why"
          value={reason}
          onChange={(v) => setReason(v as InactiveReason)}
          options={INACTIVE_REASONS.map((r) => ({ value: r, label: INACTIVE_REASON_WORDS[r] }))}
        />
        <InputText
          label="Note"
          value={note}
          placeholder="Moved to Baton Rouge in March"
          onChange={(e) => setNote(e.target.value)}
        />
        {c.activeJobs ? (
          <Banner type="warning">{plural(c.activeJobs, "active job")} stay on the schedule until you end them.</Banner>
        ) : null}
      </div>
    </Modal>
  );
}

function MergeSheet({ c, open, onClose }: { c: ClientDetail; open: boolean; onClose: () => void }) {
  const { company } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const merge = useMergeClient();
  const others = useClients(company.id, { status: "all" }, open);
  const [keep, setKeep] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const keepRow = others.data?.items.find((x) => x.id === keep);
  return (
    <Modal
      title="Merge two clients"
      open={open}
      onClose={() => {
        setProblem(null);
        onClose();
      }}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            icon="merge"
            disabled={!keep}
            loading={merge.isPending}
            onClick={() =>
              merge.mutate(
                { id: c.id, into: keep },
                {
                  onSuccess: (a) => {
                    onClose();
                    const m = a.moved;
                    toast(
                      `Merged. Moved ${plural(m.jobs, "job")}, ${plural(m.contacts, "contact")} and ${plural(m.notes, "note")}.`,
                    );
                    void navigate({ to: "/clients/$id", params: { id: a.kept } });
                  },
                  onError: (e) => setProblem(errText(e)),
                },
              )
            }
          >
            Merge
          </Button>
        </>
      }
    >
      <div className="stack">
        <p>
          <b>{c.name}</b> gets closed. Its jobs, visits, contacts, notes and files move to the client you keep.
        </p>
        <Combobox
          label="Client to keep"
          value={keep}
          onChange={(v) => {
            setKeep(v);
            setProblem(null);
          }}
          options={(others.data?.items ?? [])
            .filter((x) => x.id !== c.id)
            .map((x) => ({ value: x.id, label: `${x.name}${x.address ? ` · ${x.address}` : ""}` }))}
        />
        {keepRow ? (
          <p className="muted">
            {keepRow.name} keeps its own name, phone and email. Matching phones and addresses are not copied twice.
          </p>
        ) : null}
        {problem ? <Banner type="error">{problem}</Banner> : null}
        <p className="muted">This cannot be undone from here.</p>
      </div>
    </Modal>
  );
}

// Form ---------------------------------------------------------------------------------------------------------------
